/**
 * Back2People Frontend API Client
 * 
 * This client uses the shared index schema to ensure data consistency
 * with the backend API.
 * 
 * Created by: Zee
 */

// Note: Shared index schema is available in backend
// Frontend uses this client which matches the index structure
// Production API - Mango Business Solutions unified backend
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.mango-business.solutions/api/v1';

/**
 * Base API request handler with index validation
 */
class APIClient {
  constructor(baseURL = API_BASE_URL) {
    this.baseURL = baseURL;
  }

  /**
   * Make an API request
   * @param {string} endpoint - API endpoint
   * @param {object} options - Fetch options
   * @returns {Promise} - Response data
   */
  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    
    const defaultOptions = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      }
    };

    const config = {
      ...defaultOptions,
      ...options
    };

    try {
      console.log(`[API Client] ${config.method || 'GET'} ${endpoint}`);
      
      const response = await fetch(url, config);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'API request failed');
      }

      return data;
    } catch (error) {
      console.error(`[API Client] Error:`, error);
      throw error;
    }
  }

  /**
   * GET request
   */
  async get(endpoint) {
    return this.request(endpoint, { method: 'GET' });
  }

  /**
   * POST request with validation
   */
  async post(endpoint, data) {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  /**
   * PUT request with validation
   */
  async put(endpoint, data) {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  /**
   * DELETE request
   */
  async delete(endpoint) {
    return this.request(endpoint, { method: 'DELETE' });
  }
}

// Create singleton instance
const apiClient = new APIClient();

// ============================================================================
// EVENTS API
// ============================================================================

export const eventsAPI = {
  /**
   * Get all events (public events only)
   * Uses unified backend: GET /api/v1/events/public
   */
  async getAll() {
    const response = await apiClient.get('/events/public');
    // Transform backend format to frontend format
    const transformedEvents = (response.events || []).map(event => ({
      id: event.id,
      title: event.name,
      slug: event.id, // Use ID as slug for now
      date: event.date,
      status: event.isActive ? (new Date(event.date) >= new Date() ? 'upcoming' : 'completed') : 'completed',
      description: event.description || '',
      location: event.location ? { venue: event.location, address: event.location } : null,
      flyer: event.coverImage || null,
      coverImage: event.coverImage,
      isActive: event.isActive,
      isPublic: event.isPublic,
      // Map other fields
      theme: event.description?.substring(0, 100) || '',
      tags: [],
      artists: [],
      gallery: event.photos || [],
      attendees: event._count?.photos || 0,
      streamRecordings: event.streamRecordings || [] // Previous stream recordings
    }));
    return {
      success: true,
      data: transformedEvents,
      pagination: response.pagination
    };
  },

  /**
   * Get event by ID (public)
   * Uses unified backend: GET /api/v1/events/public/:id
   */
  async getById(id) {
    const response = await apiClient.get(`/events/public/${id}`);
    const event = response.event;
    // Transform backend format to frontend format
    const transformed = {
      id: event.id,
      title: event.name,
      slug: event.id,
      date: event.date,
      status: event.isActive ? (new Date(event.date) >= new Date() ? 'upcoming' : 'completed') : 'completed',
      description: event.description || '',
      location: event.location ? { venue: event.location, address: event.location } : null,
      flyer: event.coverImage || null,
      coverImage: event.coverImage,
      isActive: event.isActive,
      isPublic: event.isPublic,
      // Back-2-People promotional code fields
      eventType: event.eventType || 'normal',
      isPromotional: event.isPromotional || false,
      promotionalCode: event.promotionalCode || null,
      locationHidden: event.locationHidden || false,
      theme: event.description?.substring(0, 100) || '',
      tags: [],
      artists: [],
      gallery: event.photos || [],
      attendees: event._count?.photos || 0,
      streamRecordings: event.streamRecordings || [] // Previous stream recordings
    };
    return {
      success: true,
      data: transformed
    };
  },

  /**
   * Get event by slug (not supported by backend yet, use ID)
   */
  async getBySlug(slug) {
    // For now, get all events and find by slug
    const allEvents = await this.getAll();
    const event = allEvents.data.find(e => e.slug === slug);
    return {
      success: !!event,
      data: event
    };
  },

  /**
   * Create new event (requires auth - admin only)
   */
  async create(eventData) {
    const token = localStorage.getItem('authToken');
    return apiClient.request('/events', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(eventData)
    });
  },

  /**
   * Update event (requires auth - admin only)
   */
  async update(id, eventData) {
    const token = localStorage.getItem('authToken');
    return apiClient.request(`/events/${id}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(eventData)
    });
  },

  /**
   * Delete event (requires auth - admin only)
   */
  async delete(id) {
    const token = localStorage.getItem('authToken');
    return apiClient.request(`/events/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
  },

  /**
   * Validate promotional code for event (requires auth - customer)
   * Uses unified backend: POST /api/v1/events/:id/validate-code
   */
  async validateCode(eventId, code) {
    const token = localStorage.getItem('authToken');
    if (!token) {
      throw new Error('Authentication required');
    }
    const response = await apiClient.request(`/events/${eventId}/validate-code`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ code })
    });
    return response;
  },

  /**
   * Get event by access code (public)
   * Uses unified backend: GET /api/v1/events/by-code/:code
   * This allows direct access to secret events via code on cards
   */
  async getByCode(code) {
    try {
      const response = await apiClient.get(`/events/by-code/${code}`);
      const event = response.event;
      // Transform backend format to frontend format
      const transformed = {
        id: event.id,
        title: event.name,
        slug: event.id,
        date: event.date,
        status: event.isActive ? (new Date(event.date) >= new Date() ? 'upcoming' : 'completed') : 'completed',
        description: event.description || '',
        location: event.location || null,
        flyer: event.coverImage || null,
        coverImage: event.coverImage,
        isActive: event.isActive,
        isPublic: event.isPublic,
        eventType: event.eventType || 'normal',
        isPromotional: event.isPromotional || false,
        promotionalCode: event.promotionalCode || null,
        locationHidden: event.locationHidden || false,
        theme: event.theme || event.description?.substring(0, 100) || '',
        tags: [],
        artists: event.artists || [],
        gallery: event.photos || [],
        attendees: event._count?.photos || 0,
        streamRecordings: event.streamRecordings || [],
        // Custom styling/theming for decorated pages
        customTheme: event.customTheme || null,
        customCSS: event.customCSS || null,
        customColors: event.customColors || null,
        // Secret event page specific fields
        celebrating: event.celebrating || event.culturalSignificance || null,
        culturalSignificance: event.culturalSignificance || null,
        parking: event.parking || null
      };
      return {
        success: true,
        data: transformed
      };
    } catch (error) {
      // If endpoint doesn't exist yet, try getting all events and filtering
      // This is a fallback until backend supports /events/by-code/:code
      console.warn('Direct code lookup not available, trying fallback method');
      const allEvents = await this.getAll();
      const event = allEvents.data.find(e => e.promotionalCode === code || e.accessCode === code);
      if (event) {
        return {
          success: true,
          data: event
        };
      }
      throw error;
    }
  }
};

// ============================================================================
// RECORDINGS API
// ============================================================================

export const recordingsAPI = {
  /**
   * Get recordings for an event
   */
  async getByEvent(eventId) {
    const response = await apiClient.get(`/recordings/event/${eventId}`);
    return {
      success: true,
      data: response.data || []
    };
  },

  /**
   * Get recording details
   */
  async getById(id) {
    const response = await apiClient.get(`/recordings/${id}`);
    return {
      success: true,
      data: response.data
    };
  },

  /**
   * Get playback URL
   */
  async getPlaybackUrl(id) {
    const response = await apiClient.get(`/recordings/${id}/playback`);
    return {
      success: true,
      data: response.data
    };
  },

  /**
   * Get snippets from a recording
   */
  async getSnippets(recordingId) {
    const response = await apiClient.get(`/recordings/${recordingId}/snippets`);
    return {
      success: true,
      data: response.data || []
    };
  },

  /**
   * Get all public recordings
   */
  async getPublic() {
    const response = await apiClient.get('/recordings/public');
    return {
      success: true,
      data: response.data || []
    };
  }
};

// ============================================================================
// ARTISTS API
// ============================================================================

export const artistsAPI = {
  /**
   * Get all artists
   * Matches: APIEndpointIndex.artists.getAll
   */
  async getAll() {
    return apiClient.get('/artists');
  },

  /**
   * Get artist by ID
   * Matches: APIEndpointIndex.artists.getById
   */
  async getById(id) {
    return apiClient.get(`/artists/${id}`);
  },

  /**
   * Create new artist
   * Matches: APIEndpointIndex.artists.create
   * Validates against: ArtistIndex
   */
  async create(artistData) {
    return apiClient.post('/artists', artistData);
  },

  /**
   * Update artist
   * Matches: APIEndpointIndex.artists.update
   * Validates against: ArtistIndex
   */
  async update(id, artistData) {
    return apiClient.put(`/artists/${id}`, artistData);
  }
};

// ============================================================================
// GALLERY API
// ============================================================================

export const galleryAPI = {
  /**
   * Get all gallery items
   * Matches: APIEndpointIndex.gallery.getAll
   */
  async getAll() {
    return apiClient.get('/gallery');
  },

  /**
   * Get gallery items by event
   * Matches: APIEndpointIndex.gallery.getByEvent
   */
  async getByEvent(eventId) {
    return apiClient.get(`/gallery/event/${eventId}`);
  },

  /**
   * Create gallery item
   * Matches: APIEndpointIndex.gallery.create
   * Validates against: GalleryItemIndex
   */
  async create(galleryData) {
    return apiClient.post('/gallery', galleryData);
  }
};

// ============================================================================
// FLYERS API
// ============================================================================

export const flyersAPI = {
  /**
   * Get all flyers
   * Matches: APIEndpointIndex.flyers.getAll
   */
  async getAll() {
    return apiClient.get('/flyers');
  },

  /**
   * Get flyer by event
   * Matches: APIEndpointIndex.flyers.getByEvent
   */
  async getByEvent(eventId) {
    return apiClient.get(`/flyers/event/${eventId}`);
  }
};

// ============================================================================
// CONTENT API (Combined)
// ============================================================================

export const contentAPI = {
  /**
   * Get all content (gallery, flyers, recordings)
   * Matches: APIEndpointIndex.content.getAll
   */
  async getAll() {
    return apiClient.get('/content');
  }
};

// ============================================================================
// RSVP API
// ============================================================================

export const rsvpAPI = {
  /**
   * Create RSVP
   * Matches: APIEndpointIndex.rsvp.create
   * Validates against: RSVPIndex
   */
  async create(rsvpData) {
    return apiClient.post('/rsvp', rsvpData);
  },

  /**
   * Get RSVPs by event
   * Matches: APIEndpointIndex.rsvp.getByEvent
   */
  async getByEvent(eventId) {
    return apiClient.get(`/rsvp/event/${eventId}`);
  },

  /**
   * Update notification status
   * Matches: APIEndpointIndex.rsvp.updateNotification
   */
  async updateNotification(id, notificationData) {
    return apiClient.put(`/rsvp/${id}/notification`, notificationData);
  },

  /**
   * Calculate travel time for location reveal
   */
  async calculateTravelTime(fromLocation, toLocation, eventDate) {
    return apiClient.post('/rsvp/calculate-travel-time', {
      fromLocation,
      toLocation,
      eventDate
    });
  },

  /**
   * Schedule location reveal
   */
  async scheduleLocationReveal(rsvpId, revealData) {
    return apiClient.post(`/rsvp/${rsvpId}/schedule-reveal`, revealData);
  },

  /**
   * Get location reveal status for event
   */
  async getLocationRevealStatus(eventId) {
    const token = localStorage.getItem('authToken');
    return apiClient.request(`/rsvp/event/${eventId}/reveal-status`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
  },

  /**
   * Manually trigger location reveal
   */
  async triggerLocationReveal(eventId, options = {}) {
    const token = localStorage.getItem('authToken');
    return apiClient.request(`/rsvp/event/${eventId}/trigger-reveal`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(options)
    });
  }
};

// ============================================================================
// USERS/SUBSCRIBERS API
// ============================================================================

export const usersAPI = {
  /**
   * Subscribe to newsletter
   * Matches: APIEndpointIndex.users.subscribe
   */
  async subscribe(subscribeData) {
    return apiClient.post('/subscribe', subscribeData);
  },

  /**
   * Unsubscribe from newsletter
   * Matches: APIEndpointIndex.users.unsubscribe
   */
  async unsubscribe(email) {
    return apiClient.post('/unsubscribe', { email });
  }
};

// ============================================================================
// TICKETS API
// ============================================================================

export const ticketsAPI = {
  /**
   * Create ticket (purchase)
   * Bypasses external payment - test internal flow only
   */
  async create(ticketData) {
    const token = localStorage.getItem('authToken');
    return apiClient.request('/tickets', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(ticketData)
    });
  },

  /**
   * Get user's tickets
   */
  async getMy(customerId) {
    const token = localStorage.getItem('authToken');
    return apiClient.request(`/tickets/my?customerId=${customerId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
  },

  /**
   * Get ticket by ID
   */
  async getById(id, customerId) {
    const token = localStorage.getItem('authToken');
    return apiClient.request(`/tickets/${id}?customerId=${customerId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
  },

  /**
   * Get ticket QR code
   */
  async getQRCode(ticketId, customerId) {
    const token = localStorage.getItem('authToken');
    return apiClient.request(`/tickets/${ticketId}/qrcode?customerId=${customerId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
  },

  /**
   * Validate scanned ticket
   */
  async validate(scanData) {
    const token = localStorage.getItem('authToken');
    return apiClient.request('/tickets/validate', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(scanData)
    });
  }
};

// ============================================================================
// PRODUCTS API
// ============================================================================

export const productsAPI = {
  /**
   * Get all products
   */
  async getAll(filters = {}) {
    const queryParams = new URLSearchParams();
    if (filters.category) queryParams.append('category', filters.category);
    if (filters.search) queryParams.append('search', filters.search);
    const query = queryParams.toString();
    return apiClient.get(`/products${query ? `?${query}` : ''}`);
  },

  /**
   * Get product by ID
   */
  async getById(id) {
    return apiClient.get(`/products/${id}`);
  },

  /**
   * Get products by category
   */
  async getByCategory(category) {
    return apiClient.get(`/products/category/${category}`);
  },

  /**
   * Create product (admin only)
   */
  async create(productData) {
    const token = localStorage.getItem('authToken');
    return apiClient.request('/products', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(productData)
    });
  },

  /**
   * Update product (admin only)
   */
  async update(id, productData) {
    const token = localStorage.getItem('authToken');
    return apiClient.request(`/products/${id}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(productData)
    });
  },

  /**
   * Delete product (admin only)
   */
  async delete(id) {
    const token = localStorage.getItem('authToken');
    return apiClient.request(`/products/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
  }
};

// ============================================================================
// ORDERS API
// ============================================================================

export const ordersAPI = {
  /**
   * Create order (bypass payment for testing)
   */
  async create(orderData) {
    const token = localStorage.getItem('authToken');
    return apiClient.request('/orders', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(orderData)
    });
  },

  /**
   * Get user's orders
   */
  async getMy() {
    const token = localStorage.getItem('authToken');
    return apiClient.request('/orders/my', {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
  },

  /**
   * Get order by ID
   */
  async getById(id) {
    const token = localStorage.getItem('authToken');
    return apiClient.request(`/orders/${id}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
  }
};

// ============================================================================
// CONTACT/MESSAGING API
// ============================================================================

export const contactAPI = {
  /**
   * Send contact form message
   */
  async sendMessage(messageData) {
    return apiClient.post('/contact', messageData);
  }
};

export const messagesAPI = {
  /**
   * Get user's messages
   */
  async getAll() {
    const token = localStorage.getItem('authToken');
    return apiClient.request('/messages', {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
  },

  /**
   * Send user-to-user message
   */
  async send(messageData) {
    const token = localStorage.getItem('authToken');
    return apiClient.request('/messages', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(messageData)
    });
  },

  /**
   * Get message by ID
   */
  async getById(id) {
    const token = localStorage.getItem('authToken');
    return apiClient.request(`/messages/${id}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
  },

  /**
   * Mark message as read
   */
  async markAsRead(id) {
    const token = localStorage.getItem('authToken');
    return apiClient.request(`/messages/${id}/read`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
  },

  /**
   * Get conversations list
   */
  async getConversations() {
    const token = localStorage.getItem('authToken');
    return apiClient.request('/messages/conversations', {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
  }
};

// ============================================================================
// EXPORT DEFAULT CLIENT
// ============================================================================

const api = {
  events: eventsAPI,
  artists: artistsAPI,
  gallery: galleryAPI,
  flyers: flyersAPI,
  content: contentAPI,
  rsvp: rsvpAPI,
  users: usersAPI,
  recordings: recordingsAPI,
  tickets: ticketsAPI,
  products: productsAPI,
  orders: ordersAPI,
  contact: contactAPI,
  messages: messagesAPI,
  
  // Raw client for custom requests
  client: apiClient
};

export default api;

